import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import { Button } from '../components/primitives';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './Contact.css';

const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const Contact = () => {
  const { t } = useTranslation();
  const { storeEmail, storePhone } = useSiteSettings();

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [validationError, setValidationError] = useState(null);

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (validationError) setValidationError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError(null);

    const trimmed = {
      name: form.name.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(),
      message: form.message.trim(),
    };

    if (!trimmed.name || !trimmed.email || !trimmed.subject || !trimmed.message || !validEmail(trimmed.email)) {
      setValidationError(t('contact.form.validation'));
      return;
    }

    setStatus('submitting');

    try {
      await api.post('/api/contact', trimmed);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) {
        setValidationError(err?.response?.data?.error || t('contact.form.error'));
        setStatus('idle');
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="contact-page">
      <SEO title={t('contact.title')} description={t('contact.lead')} />

      <header className="contact-hero">
        <span className="contact-eyebrow">{t('contact.eyebrow')}</span>
        <h1 className="contact-title">{t('contact.title')}</h1>
        <p className="contact-lead">{t('contact.lead')}</p>
      </header>

      <div className="contact-grid">
        <aside className="contact-info" aria-label={t('contact.title')}>
          {storeEmail && (
            <div className="contact-info__item">
              <span className="contact-info__icon" aria-hidden="true">
                <Icon.Email size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} />
              </span>
              <div>
                <span className="contact-info__label">{t('contact.email')}</span>
                <a className="contact-info__value" href={`mailto:${storeEmail}`}>{storeEmail}</a>
              </div>
            </div>
          )}

          {storePhone && (
            <div className="contact-info__item">
              <span className="contact-info__icon" aria-hidden="true">
                <Icon.Phone size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} />
              </span>
              <div>
                <span className="contact-info__label">{t('contact.phone')}</span>
                <a
                  className="contact-info__value"
                  href={`tel:${storePhone.replace(/\s+/g, '')}`}
                >
                  {storePhone}
                </a>
              </div>
            </div>
          )}

          <div className="contact-info__item">
            <span className="contact-info__icon" aria-hidden="true">
              <Icon.Location size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} />
            </span>
            <div>
              <span className="contact-info__label">{t('contact.hours')}</span>
              <span className="contact-info__value">{t('contact.hoursValue')}</span>
            </div>
          </div>
        </aside>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <h2 className="contact-form__title">{t('contact.form.title')}</h2>

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label htmlFor="contact-name">{t('contact.form.name')}</label>
              <input
                id="contact-name"
                type="text"
                value={form.name}
                onChange={update('name')}
                placeholder={t('contact.form.namePlaceholder')}
                autoComplete="name"
                required
              />
            </div>
            <div className="contact-form__field">
              <label htmlFor="contact-email">{t('contact.form.email')}</label>
              <input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder={t('contact.form.emailPlaceholder')}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="contact-form__field">
            <label htmlFor="contact-subject">{t('contact.form.subject')}</label>
            <input
              id="contact-subject"
              type="text"
              value={form.subject}
              onChange={update('subject')}
              placeholder={t('contact.form.subjectPlaceholder')}
              required
            />
          </div>

          <div className="contact-form__field">
            <label htmlFor="contact-message">{t('contact.form.message')}</label>
            <textarea
              id="contact-message"
              rows={6}
              value={form.message}
              onChange={update('message')}
              placeholder={t('contact.form.messagePlaceholder')}
              required
            />
          </div>

          {validationError && (
            <div className="contact-form__alert contact-form__alert--error" role="alert">
              {validationError}
            </div>
          )}

          {status === 'success' && (
            <div className="contact-form__alert contact-form__alert--success" role="status">
              {t('contact.form.success')}
            </div>
          )}

          {status === 'error' && (
            <div className="contact-form__alert contact-form__alert--error" role="alert">
              {t('contact.form.error')}
            </div>
          )}

          <Button
            type="submit"
            variant="brand"
            size="md"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? t('contact.form.submitting') : t('contact.form.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
